import React, { useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import Scrollable from "composants/Scrollable";
import { isOnlyOfficeEnabled, URL_DATA, URL_DOWNLOAD_VIA_GED } from "customGlobal";
import { getHours, getMinutes, parseISO } from "date-fns";

import { addMessage } from "actions/messages";
import { fetchDocumentData, deleteDocument } from "api";
import { t } from "utils/i18n";
import TabHeader from "./TabHeader";
import { Message } from "types/Message";
import { TabSatelliteProps } from "containers/satellites/SatellitesData";
import FileUpload from "../file/FileUpload";
import { Dropdown, DropdownButton, DropdownMenu } from "../DropDown/Dropdown";
import { Button } from "../button";
import { Fa } from "composants/Icon";
import { formatDate } from "utils/i18n";
import auth from "auth";
import DocumentViewer, { DocumentLightDto } from "./DocumentViewer";
import OnlyOfficeEditor from "./OnlyOfficeEditor";
import { Link } from "react-router-dom";
import DocumentAiAnalysis from "./DocumentAiAnalysis";
import { DOCUMENTS } from "constant/satellite";
import { useInfiniteQuerySatellite } from "hooks/useInfiniteQuerySatellite";
import InfiniteList from "containers/listegenerique/InfiniteList";
import { tw } from "twind";

export interface Document {
  id: string;
  nom: string;
  nomGed: string;
  date: string;
  applicable: boolean;
  url: string;
  path: string;
  mimeType: string;
  codeGed: string;
}

const EDITABLE_EXTENSIONS = new Set(["doc", "docx", "xlsx", "pptx", "rtf"]);

function getFileExtension(str: string) {
  return str.split(".").pop()?.toLowerCase() ?? "";
}

function isEditableDocument(nom: string) {
  return EDITABLE_EXTENSIONS.has(getFileExtension(nom));
}

function backSlashToSlash(str: string) {
  return str.replace(/\\/g, "/");
}

function OnlyOfficeEditButton(props: { file: Document; onEdit: (file: Document) => void }) {
  if (!isOnlyOfficeEnabled() || !isEditableDocument(props.file.nom)) {
    return null;
  }

  return (
    <a
      className="level-item button is-text is-link is-inverted"
      onClick={() => props.onEdit(props.file)}
      title={t("commun_editer_onlyoffice", { defaultValue: "Éditer avec OnlyOffice" })}
    >
      <span className="icon is-small">
        <Fa icon="browser" />
      </span>
    </a>
  );
}

function UpdateFile(props: { file: Document }) {
  const url = useMemo(() => {
    const i = getFileExtension(props.file.nom);
    const urlClear = backSlashToSlash(props.file.path);
    if (i === "doc" || i === "docx" || i === "rtf") {
      return `ms-word:ofe|u|${urlClear}`;
    } else if (i === "pptx") {
      return `ms-powerpoint:ofe|u|${urlClear}`;
    } else if (i === "xlsx") {
      return `ms-excel:ofe|u|${urlClear}`;
    } else {
      return null;
    }
  }, [props.file.nom, props.file.path]);

  return (
    <>
      {url != null ? (
        <a className="level-item button is-text is-link is-inverted" href={url}>
          <span className="icon is-small">
            <Fa icon="pen" />
          </span>
        </a>
      ) : (
        <></>
      )}
    </>
  );
}

function formatDocumentDate(date: string) {
  const parsedDate = parseISO(date);
  if (getHours(parsedDate) === 0 && getMinutes(parsedDate) === 0) {
    return formatDate(date, {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  }

  return formatDate(date, {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function TabDocument({
  contextId,
  tableName,
  count,
  countAction,
  sjmoCode,
  height
}: TabSatelliteProps) {
  const loadMoreButtonRef = React.useRef<HTMLDivElement | null>(null);
  const [documentLight, setDocumentLight] = useState<DocumentLightDto>({ mimeType: "" });
  const [displayedDocument, setDisplayedDocument] = useState<Document | undefined>(undefined);
  const [editingDocument, setEditingDocument] = useState<Document | undefined>(undefined);
  const [documentToAnalyze, setDocumentToAnalyze] = useState<Document | undefined>(undefined);

  const {
    data: documents,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch
  } = useInfiniteQuerySatellite(DOCUMENTS, tableName, contextId, fetchDocumentData);

  const onClose = () => {
    setDisplayedDocument(undefined);
  };

  const onCloseEditor = () => {
    setEditingDocument(undefined);
    refetch();
  };

  const onCloseAnalysis = () => {
    setDocumentToAnalyze(undefined);
  };

  const onAnalyzeDocument = (document: Document) => {
    setDocumentToAnalyze(document);
  };

  const onDeleteDocument = (document: Document) => {
    return deleteDocument(document.id)
      .then(() => {
        refetch();
        countAction(tableName, contextId);
      })
      .catch(e => {
        const er = e as AxiosError<any>;
        if (!er.response) {
          return;
        }

        const message: Message = {
          code: er.response.data.code,
          message: t(er.response.data.message),
          type: er.response.data.type,
          target: er.response.data.target
        };
        addMessage(message);
      });
  };

  // Vérifie si un document est analysable par l'IA
  const isDocumentAnalyzable = (document: Document) => {
    const extension = document.nom
      .split(".")
      .pop()
      ?.toLowerCase();
    return extension === "pdf" || extension === "docx" || extension === "xlsx";
  };

  const buildLeftArea = (document: Document, disabledPreview: boolean) => {
    if (!disabledPreview && document.mimeType.includes("image")) {
      return (
        <a
          className="level-item"
          onClick={() => {
            setDisplayedDocument(document);
          }}
        >
          <figure className="image is-128x128">
            <img
              src={`${URL_DATA()}/document/${document.id}?&access_token=${auth.token}`}
              alt={document.nomGed}
            />
          </figure>
        </a>
      );
    } else if (!disabledPreview && document.mimeType.includes("pdf")) {
      return (
        <a
          className="level-item"
          onClick={() => {
            setDisplayedDocument(document);
          }}
        >
          <h1 className="title" style={{ width: 128 }}>
            <Fa icon="file-pdf" />
          </h1>
        </a>
      );
    } else if (
      disabledPreview &&
      (document.mimeType.includes("pdf") || document.mimeType.includes("image"))
    ) {
      return (
        <span className="fa-stack fa-lg" style={{ width: 128 }}>
          <Fa icon="file" className="fa-stack-1x" />
          <Fa icon="ban" transform="rotate-90" className="fa-stack-2x has-text-danger" />
        </span>
      );
    } else {
      return (
        <h1 className="title" style={{ width: 128 }}>
          {document.mimeType.substring(document.mimeType.indexOf("/") + 1)}
        </h1>
      );
    }
  };

  const buildDocumentCard = document => {
    let disabledPreview = false;
    const mimeType = document.mimeType ? document.mimeType : "/";

    if ((!mimeType.includes("image") && !mimeType.includes("pdf")) || document.url === null) {
      disabledPreview = true;
    }

    return (
      <div className="box donnees-satellite-container" key={document.id}>
        <article className="media">
          <div className="media-left">{buildLeftArea(document, disabledPreview)}</div>
          <div className="media-content">
            <div className="content">
              <p>
                <strong>{document.nomGed} </strong>
                <span>
                  <Fa
                    icon={document.applicable ? "check" : "times"}
                    className={document.applicable ? "has-text-success" : "has-text-danger"}
                    title={t("commun_document_applicable")}
                  />
                </span>
              </p>
              <p>{formatDocumentDate(document.date)}</p>
              <p>{document.nom}</p>
            </div>
            <nav className="level is-mobile">
              <div className="level-left">
                {disabledPreview && (
                  <div className="level-item button is-text has-text-grey-light">
                    <span className="icon is-small">
                      <Fa icon="eye-slash" />
                    </span>
                  </div>
                )}
                {!disabledPreview && (
                  <a
                    className="level-item button is-text is-link is-inverted"
                    onClick={() => {
                      setDisplayedDocument(document);
                    }}
                  >
                    <span className="icon is-small">
                      <Fa icon="eye" />
                    </span>
                  </a>
                )}
                {document.url && <UpdateFile file={document} />}
                {document.url && <OnlyOfficeEditButton file={document} onEdit={setEditingDocument} />}
                {document.url && (
                  <a
                    className="level-item button is-text is-link is-inverted"
                    href={`${URL_DOWNLOAD_VIA_GED()}/document/${document.id}?access_token=${auth.token
                      }&download=true`}
                  >
                    <span className="icon is-small">
                      <Fa icon="download" />
                    </span>
                  </a>
                )}
                {!document.url && (
                  <div className="level-item button is-text has-text-grey-light">
                    <span className="icon is-small">
                      <Fa icon="download" />
                    </span>
                  </div>
                )}
                {isDocumentAnalyzable(document) && (
                  <a
                    className="level-item button is-text is-link is-inverted"
                    onClick={() => onAnalyzeDocument(document)}
                    title={t("commun_analyser_document_ia")}
                  >
                    <span className="icon is-small">
                      <Fa icon="robot" />
                    </span>
                  </a>
                )}
                <Link
                  to={`/page/GED_DOCUMENT/` + document.codeGed}
                  className="level-item button is-text is-link is-inverted"
                >
                  <span className="icon">
                    <Fa icon="external-link" />
                  </span>
                </Link>
                <Dropdown autoclose className="is-right">
                  <DropdownButton
                    render={param => (
                      <Button className="is-text" ref={param.buttonRef} onClick={param.onOpen}>
                        <span className="icon">
                          <Fa icon="trash" />
                        </span>
                      </Button>
                    )}
                  />
                  <DropdownMenu
                    render={param => (
                      <div className="dropdown-item">
                        <p>{t("commun_sur_de_suprimer")}</p>
                        <button
                          className="button is-danger is-fullwidth is-small"
                          onClick={() => {
                            onDeleteDocument(document).then(param.onClose);
                          }}
                        >
                          {t("commun_oui")}
                        </button>
                      </div>
                    )}
                  />
                </Dropdown>
              </div>
            </nav>
          </div>
        </article>
      </div>
    );
  };

  useEffect(() => {
    if (!displayedDocument) setDocumentLight({ mimeType: "" });
    else {
      setDocumentLight({
        id: displayedDocument.id,
        name: displayedDocument.nomGed,
        mimeType: displayedDocument.mimeType
      });
    }
  }, [displayedDocument]);

  if (editingDocument) {
    return (
      <OnlyOfficeEditor
        documentId={editingDocument.id}
        documentName={editingDocument.nomGed}
        onClose={onCloseEditor}
        height="calc(70vh - 40px)"
      />
    );
  }

  return (
    <>
      <TabHeader
        i18nKey="commun_documents_liees"
        count={count}
        tableName={tableName}
        contextId={contextId}
        sjmoCode={sjmoCode}
      />
      <FileUpload
        tableName={tableName}
        contextId={contextId}
        sjmoCode={sjmoCode}
        onFileUploadComplete={() => {
          refetch();
          countAction(tableName, contextId);
        }}
      />
      <Scrollable autoHide style={{ height: height }}>
        <InfiniteList
          data={documents?.pages}
          loadMoreButtonRef={loadMoreButtonRef}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          wrapperClassName={tw("flex flex-wrap")}
          renderCell={pojo => (
            <div className={tw("w-1/2 p-2 flex")}>
              <div className={tw("flex-1")}>{buildDocumentCard(pojo)}</div>
            </div>
          )}
          listHeight={height as number}
        />
      </Scrollable>
      {displayedDocument && <DocumentViewer document={documentLight} onClose={onClose} />}
      {documentToAnalyze && (
        <DocumentAiAnalysis document={documentToAnalyze} onClose={onCloseAnalysis} />
      )}
    </>
  );
}

export default TabDocument;
